import {AssistantResponse, isClarifyingPresentation} from '../assistant/assistantResponseContract'
import {AttendanceV2Payload} from './attendanceV2Types'
import {
  buildAttendanceV2PayloadFromData,
  cleanAttendanceAnswerText,
  parseAttendanceV2Answer,
} from './parseAttendanceV2Answer'

export type AttendanceAnswerView = {
  /** The prose to show in the bubble. */
  displayText: string
  /** The structured payload the v2 views render, or `null` when there is nothing to draw. */
  payload: AttendanceV2Payload | null
}

/**
 * The backend's own "this query matched no records" flag (`meta.emptyResult`), and the last word
 * on whether anything is drawn.
 *
 * An empty answer does not reliably arrive with an empty `data` block: the same "no attendance
 * records found" turn sometimes ships counts for a wider window (`fullSummary`), a roll-up whose
 * every mark is zero, or a stray `from`/`to` — so the shape-sniffing below drew a chart on some
 * empty answers and not others, which is precisely the reported inconsistency. `emptyResult` does
 * not vary with the payload's shape, so deciding on it makes the empty answer render the same way
 * every time: prose, and the widening suggestion chips, and nothing else.
 *
 * Clarify/menu turns are excluded: their `data` carries the candidates the picker renders, not
 * records, so an empty-result flag on one must not swallow the question being asked.
 */
function statesEmptyResult(contract: AssistantResponse): boolean {
  return contract.emptyResult && !isClarifyingPresentation(contract.presentation)
}

/**
 * What one answer turns into on screen.
 *
 * `presentation` decides, and it is the whole point of the response contract: `text` draws
 * prose and nothing else, so the "no attendance records found" answer that used to arrive with
 * a marker — and therefore with a report card, a chart and export controls drawn over an empty
 * result — now draws exactly the sentence it is.
 *
 * The `parseAttendanceV2Answer` branch is the pre-contract fallback and runs ONLY when a
 * response states no presentation at all. It goes with the marker: see that function's note.
 */
export function resolveAttendanceAnswerView(
  contract: AssistantResponse,
  answer: string
): AttendanceAnswerView {
  const isEmptyResult = statesEmptyResult(contract)

  if (contract.presentation) {
    const displayText = cleanAttendanceAnswerText(contract.answer || answer)
    return {
      displayText: displayText || answer,
      // `text` states there is no widget; `steps` renders from `data.steps`, not from a v2
      // payload. Everything else draws whatever `data` actually carries — an empty `data` draws
      // nothing, which is the case the contract exists to get right.
      payload:
        isEmptyResult || contract.presentation === 'text' || contract.presentation === 'steps'
          ? null
          : buildAttendanceV2PayloadFromData(contract.data, displayText),
    }
  }

  const legacy = parseAttendanceV2Answer(answer)
  return {
    displayText:
      legacy.narrative?.trim() ||
      legacy.payload?.narrative?.trim() ||
      legacy.payload?.error?.trim() ||
      answer,
    // The marker is dual-emitted on empty answers too, so a pre-contract backend that still
    // states `emptyResult` gets the same suppression the contract path gets.
    payload: isEmptyResult ? null : legacy.payload,
  }
}
